import { Formik, Form, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import { PaperPlaneIcon, SMSIconFilled } from '@rsos/assets/icons';
import { AttentionTriangleIcon } from '@rsos/base-assets/icons';
import { Box, Button, Text, TextArea } from '@rsos/base-components';
import { useUpdateEventShareMutation } from '@rsos/reduxDataStore/src/features/eventShare/eventShareApi';
import { EventShare } from '@rsos/reduxDataStore/src/features/eventShare/types';
import { extractErrorMessage } from '@rsos/reduxDataStore/src/utils/errorUtils';
import getInitials from '@rsos/utils/getInitials';
import { displayMilitaryTime } from '@rsos/utils/timeHelpers';
import { notifyError, notifySuccess } from '@rsos/utils/toastUtils';
import {
  CommentWrapper,
  Avatar,
  Comment,
} from './EventShareAccordionContent.styles';

const commentValidationSchema = Yup.object({
  comment: Yup.string().max(280, 'Comment must be 280 characters or less'),
});

const initialValues = {
  comment: '',
};

const AdditionalComments = ({
  share,
  isActive,
}: {
  share: EventShare;
  isActive: boolean;
}) => {
  const [updateEventShare] = useUpdateEventShareMutation();

  const commentCount = share.additional_comments.length;
  const isCommentLimitReached = commentCount >= 5;

  const handleSubmit = async (
    values: typeof initialValues,
    { resetForm, setSubmitting }: FormikHelpers<typeof initialValues>,
  ) => {
    if (!values.comment.trim()) {
      setSubmitting(false);
      return;
    }

    try {
      await updateEventShare({
        org: share.organization,
        sessionId: share.session_id,
        uri: share.uri,
        additional_comments: [values.comment],
      }).unwrap();

      resetForm();
      notifySuccess('Comment added successfully');
    } catch (error: unknown) {
      const errorMessage = extractErrorMessage(error, 'Failed to add comment');
      notifyError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box flow={16}>
      <Box horizontal flow={4} align="center" color="primaryText">
        <SMSIconFilled height={14} width={14} />
        <Text fontWeight="bold" size="normal">
          Additional Comments
        </Text>
      </Box>
      <CommentWrapper>
        {share.additional_comments.map(comment => (
          <Comment key={comment.id}>
            <Avatar>
              <Text size="xsmall">
                {getInitials(
                  comment.created_by?.name || 'Unknown',
                  'uppercase',
                )}
              </Text>
            </Avatar>
            <Box flow={6} flex={1}>
              <Text color="primaryText" size="normal">
                {comment.created_by?.name || 'Unknown User'}
              </Text>
              <Text fontWeight="regular" size="normal" color="secondaryText">
                {comment.comment}
              </Text>
            </Box>
            <Text color="secondaryText" size="small">
              {displayMilitaryTime(comment.created_at)}
            </Text>
          </Comment>
        ))}
      </CommentWrapper>

      {isActive && (
        <Formik
          initialValues={initialValues}
          validationSchema={commentValidationSchema}
          onSubmit={handleSubmit}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            isSubmitting,
          }) => (
            <Form>
              <Box flow={8}>
                <Box>
                  <TextArea
                    name="comment"
                    placeholder={
                      isCommentLimitReached
                        ? 'Comment limit reached'
                        : 'Add a comment for the field'
                    }
                    value={values.comment}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    height="78px"
                    maxLength={280}
                    isDisabled={isCommentLimitReached}
                  />
                  {errors.comment && touched.comment && (
                    <Box mt="4px">
                      <Text color="lightDanger" size="small">
                        {errors.comment}
                      </Text>
                    </Box>
                  )}
                  {isCommentLimitReached ? (
                    <Box mt="4px" horizontal flow={4} align="center">
                      <Box flex="0 0 auto">
                        <AttentionTriangleIcon
                          height={10}
                          width={10}
                          color="lightDanger"
                        />
                      </Box>
                      <Text color="lightDanger" size="small">
                        Maximum of 5 comments reached
                      </Text>
                    </Box>
                  ) : (
                    <Box mt="4px">
                      <Text color="secondaryText" size="small">
                        {values.comment.length}/280 characters
                      </Text>
                    </Box>
                  )}
                </Box>
                <Box align="end">
                  <Button
                    btnType="primary"
                    type="submit"
                    size="xsmall"
                    noMargin
                    disabled={
                      isSubmitting ||
                      !values.comment.trim() ||
                      isCommentLimitReached
                    }
                  >
                    <Box horizontal align="center" flow={4}>
                      <PaperPlaneIcon />
                      <Text>{isSubmitting ? 'Adding...' : 'Add Comment'}</Text>
                    </Box>
                  </Button>
                </Box>
              </Box>
            </Form>
          )}
        </Formik>
      )}
    </Box>
  );
};

export default AdditionalComments;
